// ────────────────────────────────────────────────────────────────────────────
// Merged notification channel model.
//
// ONE Settings section, a single row per EVENT TYPE, each row exposing a
// channel choice: Off / In-app / Push.
//
// 2026-09-11: PUSH IS LIVE AGAIN. Local notifications reached the owner's
// iPhone for the first time that day (the plugin proxy had been awaited, see
// lib/notifications.ts), APNs sends have been landing since 2026-07-01, and
// every kind the worker writes an in-app row for now carries a push type
// (worker lib/notify.ts, migration 0087). Every account row is push eligible.
//
// Two SCOPES of event:
//   - "account": the worker raises them. In-app is gated by a field on
//     accountSettings, push by a field on notificationPreferences. Both stores
//     stay as they are; a row derives its channel from the two fields.
//   - "device": the app itself raises them on this phone (the event reminder
//     a trainer sets from a bonus card). They never touch the
//     server; the choice lives in localStorage (lib/localNotifPrefs.ts).
//     In-app means a toast while the app is open, Push means the OS banner.
//
// Channel semantics:
//   - "off"    → nothing for this event.
//   - "in-app" → toast + Notifications menu only (no OS permission needed).
//   - "push"   → an OS notification as well (APNs for account events, a local
//                notification for device events). Push implies in-app.
// ────────────────────────────────────────────────────────────────────────────

export type NotifChannel = "off" | "in-app" | "push";

/**
 * Field on `accountSettings` (in-app store) that gates in-app delivery for
 * this event, if one exists. `null` means the event has no dedicated in-app
 * gate today (its in-app delivery is not independently persisted).
 */
export type InAppField =
  | "notifFriendRequest"
  | "notifFriendAccepted"
  | "notifFriendDexProgress"
  | "notifCollectionMilestone"
  | null;

/**
 * Field on `notificationPreferences` (push store) that gates push delivery for
 * this event, if one exists. `null` means the event is in-app only and has no
 * push field (so it can never be a "push" channel).
 */
export type PushField =
  | "collectionMilestones"
  | "friendDexProgress"
  | "friendRequests"
  | "friendAccepted"
  | "trades"
  | null;

export type InAppFieldAll = InAppField | "notifTrades";

/** Where the event is raised and therefore where its choice is stored. */
export type NotifScope = "account" | "device";

/** Ids of the events the app raises on the device itself. */
export type DeviceNotifId = "event-reminders";

/** A concrete example of the notification, for the preview and the docs. */
export interface NotifExample {
  title: string;
  body: string;
}

export type NotifEvent = {
  /** Stable id used for testids and keys. */
  id: string;
  label: string;
  desc: string;
  scope: NotifScope;
  /** Whether the Push segment is offered (enabled) for this row. */
  pushEligible: boolean;
  /** Account events only: the accountSettings field that gates in-app. */
  inAppField: InAppFieldAll;
  /** Account events only: the notificationPreferences field that gates push. */
  pushField: PushField;
  /** Device events only: the default channel before the trainer chooses. */
  deviceDefault?: NotifChannel;
  /** What one of these looks like. */
  example: NotifExample;
};

/**
 * The unified event taxonomy. One row per event type, ordered most-important
 * first. Every row here has a real producer in the worker (audit 2026-07-24).
 * All rows are in-app only — push is disabled app-wide (see the header note);
 * `pushEligible` is false everywhere until APNs/FCM delivery is live.
 */
export const NOTIF_EVENTS: NotifEvent[] = [
  // ── Account events: the worker raises these ────────────────────────────
  {
    id: "friend-request",
    label: "Friend request received",
    desc: "Someone wants to connect with you on Nexus Dex",
    scope: "account",
    pushEligible: true,
    inAppField: "notifFriendRequest",
    pushField: "friendRequests",
    example: { title: "New friend request", body: "Someone wants to connect with you on Nexus Dex." },
  },
  {
    id: "friend-accepted",
    label: "Friend accepts your request",
    desc: "When someone you added confirms the friendship",
    scope: "account",
    pushEligible: true,
    inAppField: "notifFriendAccepted",
    pushField: "friendAccepted",
    example: { title: "Ash accepted your request", body: "You are now friends on Nexus Dex. See what you can trade." },
  },
  {
    id: "trades",
    label: "Trade proposals",
    desc: "A friend proposes, completes, declines or cancels a trade",
    scope: "account",
    pushEligible: true,
    inAppField: "notifTrades",
    pushField: "trades",
    example: { title: "Ash wants to trade", body: "Ash will trade you Gible for your Larvitar." },
  },
  {
    id: "friend-dex-progress",
    label: "Friend's dex grew",
    desc: "A friend caught something that unlocks a mutual trade",
    scope: "account",
    pushEligible: true,
    inAppField: "notifFriendDexProgress",
    pushField: "friendDexProgress",
    example: { title: "Ash's dex grew", body: "Ash's dex grew: your mutual trades updated" },
  },
  {
    id: "collection-milestone",
    label: "Collection milestone",
    desc: "Halfway, three quarters and complete on any dex: Pokédex, Shiny, Lucky and the rest",
    scope: "account",
    // In-app only (owner direction 2026-09-11): the trainer entered the
    // counts, so there is nothing to push to the lock screen.
    pushEligible: false,
    inAppField: "notifCollectionMilestone",
    pushField: "collectionMilestones",
    example: { title: "Shiny Dex 75% done", body: "You're 76% through your Shiny Dex. Keep the streak going!" },
  },
  // ── Device events: this phone raises these ─────────────────────────────
  {
    id: "event-reminders",
    label: "Event reminders",
    desc: "The reminders you set from an event or bonus card: a countdown card from an hour before the start (Lock Screen and Dynamic Island on iPhone, an ongoing notification on Android)",
    scope: "device",
    pushEligible: true,
    inAppField: null,
    pushField: null,
    deviceDefault: "push",
    example: { title: "1/4 transfer candy is live", body: "Candy Transfer Day just started. Time to clear your Box Cleanup batch." },
  },
];

/** The device-raised events, for the local preference store. */
export const DEVICE_NOTIF_EVENTS = NOTIF_EVENTS.filter((e) => e.scope === "device");

/**
 * A minimal view of the two stores needed to derive/patch a channel. Callers
 * pass the raw boolean values they already hold.
 */
export type NotifStoreValues = {
  inApp: Record<string, boolean | undefined>;
  push: Record<string, boolean | undefined>;
};

/**
 * Derive the current channel for an event from the two backing stores.
 *
 * Rules:
 *   - push field true                       → "push" (superset of in-app)
 *   - else in-app field true                → "in-app"
 *   - else, if event has NO in-app field but has a push field that is false,
 *     we can only represent off vs push, so false push reads as "off"
 *   - otherwise                             → "off"
 */
export function deriveChannel(event: NotifEvent, stores: NotifStoreValues): NotifChannel {
  const pushOn = event.pushField ? stores.push[event.pushField] === true : false;
  if (event.pushEligible && pushOn) return "push";

  const inAppOn = event.inAppField ? stores.inApp[event.inAppField] === true : false;
  if (inAppOn) return "in-app";

  // Events with no in-app field: their "in-app-only" state can't be persisted
  // separately, so a non-push value collapses to "off". Events with an in-app
  // field but no push field simply never reach "push".
  if (event.inAppField === null && event.pushField && pushOn) return "push";
  return "off";
}

/**
 * Compute the store patches to apply when the user picks `channel` for `event`.
 * Returns at most one patch per store; omit a store when there's nothing to
 * write for it.
 *
 *   - "off"    → in-app field false, push field false
 *   - "in-app" → in-app field true,  push field false
 *   - "push"   → in-app field true,  push field true  (push implies in-app)
 *
 * When a store has no field for the event, that store's patch is omitted.
 */
export function channelPatch(
  event: NotifEvent,
  channel: NotifChannel,
): { inAppPatch?: Record<string, boolean>; pushPatch?: Record<string, boolean> } {
  const inAppTrue = channel === "in-app" || channel === "push";
  const pushTrue = channel === "push";

  const out: { inAppPatch?: Record<string, boolean>; pushPatch?: Record<string, boolean> } = {};

  if (event.inAppField) {
    out.inAppPatch = { [event.inAppField]: inAppTrue };
  }
  if (event.pushField) {
    // Only ever set push true for push-eligible events; non-eligible events
    // keep their push field false so a demoted type never emits a push.
    out.pushPatch = { [event.pushField]: event.pushEligible ? pushTrue : false };
  }
  return out;
}

/** True when any push-eligible event is currently set to the push channel. */
export function anyPushEnabled(stores: NotifStoreValues): boolean {
  return NOTIF_EVENTS.some(
    (e) => e.pushEligible && e.pushField && stores.push[e.pushField] === true,
  );
}
